import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { CollectionData } from "@/lib/type";
import { IngredientPackaging } from "./inventory.type";
import { IngredientApi } from "./ingredient.api";

export interface IngredientPackagingWritePayload {
  name: string;
  quantityInBase: string;
  isDefault?: boolean;
}

export const IngredientPackagingApi = createApi({
  reducerPath: "ingredientPackagingApi",
  tagTypes: ["ingredientPackaging"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getIngredientPackagings: build.query<
      IngredientPackaging[],
      { ingredientUuid: string }
    >({
      query: ({ ingredientUuid }) =>
        `ingredients/${ingredientUuid}/packagings`,
      providesTags: ["ingredientPackaging"],
      transformResponse: (data: CollectionData<IngredientPackaging>) =>
        data["hydra:member"],
    }),

    getIngredientPackaging: build.query<
      IngredientPackaging,
      { uuid: string }
    >({
      query: ({ uuid }) => `ingredient_packagings/${uuid}`,
      providesTags: ["ingredientPackaging"],
    }),

    createIngredientPackaging: build.mutation<
      IngredientPackaging,
      { ingredientUuid: string; data: IngredientPackagingWritePayload }
    >({
      query: ({ ingredientUuid, data }) => ({
        url: `ingredients/${ingredientUuid}/packagings`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ingredientPackaging"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
        } catch {
          // ignore
        }
      },
    }),

    updateIngredientPackaging: build.mutation<
      IngredientPackaging,
      { uuid: string; data: Partial<IngredientPackagingWritePayload> }
    >({
      query: ({ uuid, data }) => ({
        url: `ingredient_packagings/${uuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["ingredientPackaging"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
        } catch {
          // ignore
        }
      },
    }),

    deleteIngredientPackaging: build.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `ingredient_packagings/${uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ingredientPackaging"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
        } catch {
          // ignore
        }
      },
    }),
  }),
});

export const {
  useGetIngredientPackagingsQuery,
  useGetIngredientPackagingQuery,
  useCreateIngredientPackagingMutation,
  useUpdateIngredientPackagingMutation,
  useDeleteIngredientPackagingMutation,
} = IngredientPackagingApi;
